import { ReactNode } from "react"

import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { Icon, IconExternalLink } from "@tabler/icons-react"

import { cn } from "@/lib/utils"

export type TextLinkProps = React.ComponentPropsWithRef<"a"> & {
  icon?: Icon
  iconPosition?: "left" | "right"
  hideUnderline?: boolean
  disabled?: boolean
  children: ReactNode
  render?: useRender.RenderProp
}

export function TextLink({
  icon,
  iconPosition,
  hideUnderline = false,
  disabled,
  className,
  children,
  href,
  render,
  ref,
  ...props
}: TextLinkProps) {
  const Icon = icon || IconExternalLink

  const defaultProps = {
    "aria-disabled": disabled,
    href: disabled ? undefined : href,
    rel: "noreferrer",
    className: cn(
      "inline w-fit cursor-pointer items-center aria-disabled:cursor-default [&>svg]:inline",
      "text-link hover:text-foreground aria-disabled:text-muted-foreground/50",
      !hideUnderline ? "underline underline-offset-2" : "",
      className,
    ),
    children: (
      <>
        {iconPosition === "left" && (
          <Icon size="1em" className="relative bottom-[.125em] mr-1" />
        )}
        {children}
        {iconPosition === "right" && (
          <Icon size="1em" className="relative bottom-[.125em] ml-1" />
        )}
      </>
    ),
  }

  const element = useRender({
    render,
    ref,
    defaultTagName: "a",
    props: mergeProps<"a">(defaultProps, props),
  })

  return element
}
